// deployment_class: customer_env_allowed
/**
 * API client base — shared fetch wrapper for all portal API calls. Plan 14
 * §Task 8.
 *
 * Responsibilities:
 *   - Auto-inject `Authorization: Bearer <jwt>` (when JWT is in-memory) OR
 *     rely on httpOnly cookie via `credentials: 'include'` (production
 *     path per OQ-Sh1).
 *   - Auto-generate `Idempotency-Key` UUID for POST/PATCH/PUT/DELETE.
 *   - Parse RFC 7807 problem-details responses into typed ApiError
 *     subclasses for ergonomic catch-side handling.
 *   - Propagate AbortSignal so callers can cancel in-flight requests.
 *
 * Each portal's `api-client.ts` extends this base with portal-specific
 * resource accessors.
 */

// ─────────────────────────────────────────────────────────────
// Error model — RFC 7807 problem-details + typed subclasses
// ─────────────────────────────────────────────────────────────

export interface ProblemDetail {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetail;

  constructor(status: number, problem: ProblemDetail, message?: string) {
    super(message ?? problem.title ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }

  /** Human-readable message suitable for surfacing in a toast. */
  userFacingMessage(): string {
    return this.problem.detail ?? this.problem.title ?? this.message;
  }
}

export class AuthError extends ApiError {
  constructor(problem: ProblemDetail) {
    super(401, problem, problem.title ?? 'Sign in required');
    this.name = 'AuthError';
  }
}

export class ScopeError extends ApiError {
  readonly missingScope: string | null;
  constructor(problem: ProblemDetail) {
    super(403, problem, problem.title ?? 'Insufficient scope');
    this.name = 'ScopeError';
    const claim = problem['scope'];
    this.missingScope = typeof claim === 'string' ? claim : null;
  }
}

class NotFoundError extends ApiError {
  constructor(problem: ProblemDetail) {
    super(404, problem, problem.title ?? 'Not found');
    this.name = 'NotFoundError';
  }
}

export class ValidationError extends ApiError {
  readonly errors: Record<string, string[]>;
  constructor(problem: ProblemDetail) {
    super(422, problem, problem.title ?? 'Invalid request');
    this.name = 'ValidationError';
    const fieldErrors = problem['errors'];
    this.errors =
      fieldErrors && typeof fieldErrors === 'object' && !Array.isArray(fieldErrors)
        ? (fieldErrors as Record<string, string[]>)
        : {};
  }
}

export class IdempotencyConflict extends ApiError {
  constructor(problem: ProblemDetail) {
    super(409, problem, problem.title ?? 'Idempotency-Key conflict');
    this.name = 'IdempotencyConflict';
  }
}

// ─────────────────────────────────────────────────────────────
// Auth-error global hook (TanStack Query subscribes to this in main.tsx
// at v1.5; for now ApiError instanceof AuthError is the pattern).
// ─────────────────────────────────────────────────────────────

type AuthErrorHandler = (err: AuthError) => void;
let authErrorHandler: AuthErrorHandler | null = null;

/**
 * Register a global handler called whenever an API call returns 401. The
 * AuthProvider can use this to flip the auth store to 'unauthenticated' on
 * any 401 from any endpoint, not just /v3/auth/me.
 */
export function setAuthErrorHandler(handler: AuthErrorHandler | null): void {
  authErrorHandler = handler;
}

// ─────────────────────────────────────────────────────────────
// Fetch wrapper
// ─────────────────────────────────────────────────────────────

export interface ApiFetchOptions extends RequestInit {
  /**
   * Override the base URL. Defaults to '' (same-origin, vite proxy
   * handles /v3/* in dev; identical-origin reverse proxy in prod).
   */
  baseUrl?: string;
  /**
   * Bearer token for the Authorization header. Optional — when omitted,
   * the request relies on the httpOnly cookie (credentials: 'include').
   */
  bearerToken?: string;
  /**
   * Skip the auto-generated Idempotency-Key header. Default: false (header
   * is generated for any non-GET method).
   */
  skipIdempotency?: boolean;
}

const NON_IDEMPOTENT_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

// ─────────────────────────────────────────────────────────────────────────────
// CSRF double-submit echo (S-24 · Session Hardening spec §8.1)
//
// The backend issues `__Host-kathedral-csrf` (deliberately NOT httpOnly, because
// double-submit requires a JS read) and stores its peppered hash on the session.
// The middleware enforces as soon as a session carries that hash, so this echo is
// what keeps every browser write from 403-ing. Same shape as `Idempotency-Key`
// below: non-safe methods only, never overwriting a caller-supplied value.
//
// SSOT for both names is `src/v3/server/middleware/csrf.ts`. The frontend cannot
// import across that build boundary, so the literals are duplicated here and
// PINNED by test/shell/api/csrf-echo.test.ts — rename one side without the other
// and the test fails rather than every write silently breaking.
// ─────────────────────────────────────────────────────────────────────────────

/** Non-httpOnly CSRF cookie the backend issues at session creation. */
export const CSRF_COOKIE_NAME = '__Host-kathedral-csrf';
/** Header the token is echoed in (case-insensitive on the wire). */
export const CSRF_HEADER_NAME = 'X-Kathedral-CSRF-Token';

/**
 * Extract the CSRF token from a raw `Cookie`-style string.
 *
 * Exported so the parse is testable without depending on jsdom's cookie jar,
 * which applies real `__Host-`/`Secure` rules over a non-HTTPS test origin and
 * would silently drop a seeded value — making a test pass for the wrong reason.
 *
 * Matches the cookie name EXACTLY. A prefix match would let `evil-__Host-
 * kathedral-csrf` shadow the real token.
 */
export function readCsrfTokenFrom(cookieString: string): string | undefined {
  if (!cookieString) return undefined;
  for (const part of cookieString.split(';')) {
    const idx = part.indexOf('=');
    if (idx < 0) continue;
    if (part.slice(0, idx).trim() !== CSRF_COOKIE_NAME) continue;
    const value = part.slice(idx + 1).trim();
    return value === '' ? undefined : value;
  }
  return undefined;
}

/** Read the CSRF token from the live document, if any. */
function readCsrfToken(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  return readCsrfTokenFrom(document.cookie);
}

function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for runtimes without crypto.randomUUID (jsdom < 22).
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  if (bytes[6] !== undefined) bytes[6] = (bytes[6] & 0x0f) | 0x40;
  if (bytes[8] !== undefined) bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function buildHeaders(opts: ApiFetchOptions, method: string): Headers {
  const headers = new Headers(opts.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (opts.bearerToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${opts.bearerToken}`);
  }
  const upperMethod = method.toUpperCase();
  if (!opts.skipIdempotency && NON_IDEMPOTENT_METHODS.has(upperMethod)) {
    if (!headers.has('Idempotency-Key')) {
      headers.set('Idempotency-Key', generateIdempotencyKey());
    }
  }
  // CSRF double-submit echo. Non-safe methods only (the middleware bypasses
  // GET/HEAD/OPTIONS). Absent cookie → no header, which is what keeps sessions
  // created BEFORE issuance shipped working: the middleware early-returns for a
  // session with no stored hash, so those requests must not be forced to carry a
  // token they were never given.
  if (NON_IDEMPOTENT_METHODS.has(upperMethod) && !headers.has(CSRF_HEADER_NAME)) {
    const csrfToken = readCsrfToken();
    if (csrfToken) headers.set(CSRF_HEADER_NAME, csrfToken);
  }
  // application/json body unless caller already set Content-Type or body is FormData.
  if (
    opts.body !== undefined &&
    opts.body !== null &&
    !(opts.body instanceof FormData) &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }
  return headers;
}

async function parseProblemDetail(res: Response): Promise<ProblemDetail> {
  try {
    const json = (await res.json()) as ProblemDetail;
    return json;
  } catch {
    return { status: res.status, title: res.statusText };
  }
}

function buildErrorForStatus(status: number, problem: ProblemDetail): ApiError {
  switch (status) {
    case 401:
      return new AuthError(problem);
    case 403:
      return new ScopeError(problem);
    case 404:
      return new NotFoundError(problem);
    case 409:
      return new IdempotencyConflict(problem);
    case 422:
      return new ValidationError(problem);
    default:
      return new ApiError(status, problem);
  }
}

/** What an upload knows about itself, measured rather than guessed. */
export interface UploadProgress {
  readonly loaded: number;
  readonly total: number;
  /** 0-100, integer. */
  readonly percent: number;
  /** Bytes per second over this attempt. Null until there is enough to measure. */
  readonly bytesPerSecond: number | null;
  /** Seconds left at the current rate. Null when it cannot honestly be said. */
  readonly secondsRemaining: number | null;
}

export interface ApiUploadOptions {
  readonly baseUrl?: string;
  readonly signal?: AbortSignal;
  readonly onProgress?: (progress: UploadProgress) => void;
}

/**
 * POST a body with REAL upload progress.
 *
 * `fetch` cannot report how much of a request body has been sent — there is no
 * upload-progress event on it. So a percentage derived from `fetch` can only
 * ever be 0 then 100, which is not a measurement, it is a decoration. Showing
 * one would be the same lie as a spinner: a number that looks like information
 * and carries none.
 *
 * XMLHttpRequest does expose `upload.onprogress`, so this uses it. Everything
 * else — headers, CSRF echo, idempotency, credentials, and the exact error
 * types — is the SAME code `apiFetch` runs, not a parallel copy that can drift.
 * The response is rebuilt into a `Response` purely so `parseProblemDetail` and
 * `buildErrorForStatus` can be reused verbatim.
 */
export async function apiUpload<T = unknown>(
  path: string,
  body: FormData,
  options: ApiUploadOptions = {},
): Promise<T> {
  const { baseUrl = '', signal, onProgress } = options;
  // Content-Type is deliberately NOT set: the browser must choose the multipart
  // boundary, and buildHeaders already leaves FormData alone.
  const headers = buildHeaders({ body }, 'POST');

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${baseUrl}${path}`, true);
    xhr.withCredentials = true;
    headers.forEach((value, key) => xhr.setRequestHeader(key, value));

    const startedAt = Date.now();

    xhr.upload.onprogress = (event: ProgressEvent): void => {
      if (!onProgress) return;
      // Without a computable length there is no honest percentage to report.
      if (!event.lengthComputable || event.total === 0) return;

      const elapsedSeconds = (Date.now() - startedAt) / 1000;
      // A rate measured over a few milliseconds is noise, and a "2 seconds
      // left" that immediately becomes "40 seconds left" is worse than no
      // estimate. Both stay null until the measurement means something.
      const measurable = elapsedSeconds > 0.5 && event.loaded > 0;
      const bytesPerSecond = measurable ? event.loaded / elapsedSeconds : null;
      const remainingBytes = event.total - event.loaded;

      onProgress({
        loaded: event.loaded,
        total: event.total,
        percent: Math.round((event.loaded / event.total) * 100),
        bytesPerSecond,
        secondsRemaining:
          bytesPerSecond !== null && bytesPerSecond > 0
            ? Math.ceil(remainingBytes / bytesPerSecond)
            : null,
      });
    };

    const onAbort = (): void => xhr.abort();
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
    }
    const cleanup = (): void => signal?.removeEventListener('abort', onAbort);

    xhr.onload = (): void => {
      cleanup();
      const res = new Response(xhr.responseText, {
        status: xhr.status,
        statusText: xhr.statusText,
        headers: { 'Content-Type': xhr.getResponseHeader('Content-Type') ?? 'application/json' },
      });
      if (!res.ok) {
        void parseProblemDetail(res).then((problem) => {
          const err = buildErrorForStatus(res.status, problem);
          if (err instanceof AuthError && authErrorHandler) authErrorHandler(err);
          reject(err);
        });
        return;
      }
      if (xhr.status === 204) return resolve(undefined as T);
      try {
        resolve(JSON.parse(xhr.responseText) as T);
      } catch {
        resolve(xhr.responseText as unknown as T);
      }
    };

    xhr.onerror = (): void => {
      cleanup();
      // The shape the video calls "connection lost" — indistinguishable from
      // the server side, and the only honest thing to say.
      reject(new ApiError(0, { status: 0, title: 'Connection lost' }));
    };
    xhr.onabort = (): void => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };
    xhr.ontimeout = (): void => {
      cleanup();
      reject(new ApiError(0, { status: 0, title: 'The upload timed out' }));
    };

    xhr.send(body);
  });
}

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const { baseUrl = '', bearerToken: _bearer, skipIdempotency: _skip, ...rest } = options;
  const method = (rest.method ?? 'GET').toUpperCase();
  const headers = buildHeaders(options, method);

  const res = await fetch(`${baseUrl}${path}`, {
    credentials: 'include',
    ...rest,
    method,
    headers,
  });

  if (!res.ok) {
    const problem = await parseProblemDetail(res);
    const err = buildErrorForStatus(res.status, problem);
    if (err instanceof AuthError && authErrorHandler) {
      authErrorHandler(err);
    }
    throw err;
  }

  // 204 No Content: return undefined as T (caller chooses void as T).
  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('Content-Type') ?? '';
  if (contentType.includes('application/json')) {
    return (await res.json()) as T;
  }
  // Caller asked for non-JSON; return text as T (caller's responsibility).
  return (await res.text()) as unknown as T;
}
